import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { AnimatedOutlet } from "./Motion";
import { Users, LogOut, User as UserIcon, LineChart, Home, Send, Briefcase } from "lucide-react";
import { BLUFloatingChat } from "./BLUFloatingChat";
import { readNeoPreference, STUDIO_PREFS_EVENT } from "../lib/studioPrefs";
import { AdminMobileTopNav } from "./AdminMobileTopNav";
import { NotificationBell } from "./PortalLayout";

const ADMIN_NAV = [
  { to: "/admin", end: true, label: "HOME", icon: Home, testid: "admin-nav-home" },
  { to: "/admin/engagements", end: false, label: "STUDIO", icon: Briefcase, testid: "admin-nav-roster" },
  { to: "/admin/outreach", end: false, label: "OUTREACH", icon: Send, testid: "admin-nav-outreach" },
  { to: "/admin/crm", end: false, label: "CONTACTS", icon: Users, testid: "admin-nav-crm" },
  { to: "/admin/revenue", end: false, label: "REVENUE", icon: LineChart, testid: "admin-nav-revenue" },
];

export const AdminLayout = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const fullHeightPage = false;
  const sidebarFooterRef = useRef(null);
  const mobileChromeRef = useRef(null);
  const mainScrollRef = useRef(null);
  const [neo, setNeo] = useState(() => readNeoPreference());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    try {
      localStorage.removeItem("taistu_admin_nav_variant");
    } catch {
      /* ignore */
    }
    const syncNeo = () => setNeo(readNeoPreference());
    window.addEventListener(STUDIO_PREFS_EVENT, syncNeo);
    window.addEventListener("storage", syncNeo);
    return () => {
      window.removeEventListener(STUDIO_PREFS_EVENT, syncNeo);
      window.removeEventListener("storage", syncNeo);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const el = sidebarFooterRef.current;
    if (!el) return undefined;

    const setVar = () => {
      const h = Math.round(el.getBoundingClientRect().height || 0);
      document.documentElement.style.setProperty("--admin-sidebar-footer-h", `${h}px`);
    };

    setVar();
    const ro = new ResizeObserver(setVar);
    ro.observe(el);
    window.addEventListener("resize", setVar);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", setVar);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const el = mobileChromeRef.current;
    if (!el) return undefined;

    const setVar = () => {
      const h = Math.round(el.getBoundingClientRect().height || 0);
      document.documentElement.style.setProperty("--admin-mobile-nav-h", `${h}px`);
    };

    setVar();
    const ro = new ResizeObserver(setVar);
    ro.observe(el);
    window.addEventListener("resize", setVar);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", setVar);
      document.documentElement.style.removeProperty("--admin-mobile-nav-h");
    };
  }, []);

  return (
    <div
      className="App glass-neo-lab relative flex min-h-screen flex-col max-w-full min-w-0 overflow-x-clip lg:h-dvh lg:max-h-dvh lg:flex-row lg:overflow-hidden"
      data-neo={neo}
      data-testid="admin-layout"
    >
      <div className="glass-neo-lab__wash" aria-hidden />

      <header
        ref={mobileChromeRef}
        className="relative z-10 flex min-w-0 shrink-0 flex-col lg:hidden"
        data-testid="admin-mobile-chrome"
      >
        <div className="flex items-center justify-end px-4 pt-2 sm:px-5">
          <NotificationBell testid="admin-notification-bell-mobile" />
        </div>
        <AdminMobileTopNav
          items={ADMIN_NAV}
          onSettings={() => navigate("/admin/profile")}
          onLogout={logout}
        />
      </header>

      <aside
        className="neo-shell relative z-10 hidden flex-shrink-0 flex-col overflow-hidden lg:sticky lg:top-3 lg:m-3 lg:mr-0 lg:flex lg:h-[calc(100dvh-1.5rem)] lg:w-64 xl:w-72"
        data-testid="admin-sidebar"
      >
        <div className="flex items-start justify-between gap-2 px-6 pb-2 pt-7">
          <NavLink
            to="/admin"
            end
            className="font-heading inline-block text-xl tracking-tight text-[var(--t-text)] transition-colors hover:text-[var(--t-accent)]"
            style={{ letterSpacing: "0.04em" }}
            data-testid="admin-home-link"
          >
            TAĪSTU
          </NavLink>
          <NotificationBell testid="admin-notification-bell" />
        </div>
        <nav className="flex-1 py-3" data-testid="admin-nav">
          {ADMIN_NAV.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => `neo-nav-item ${isActive ? "is-active" : ""}`}
              data-testid={testid}
            >
              <Icon size={16} strokeWidth={1.4} />
              <span className="leading-snug">{label}</span>
            </NavLink>
          ))}
        </nav>
        <div
          ref={sidebarFooterRef}
          className="mt-auto border-t border-white/40 px-5 py-6"
        >
          <button
            type="button"
            onClick={() => navigate("/admin/profile")}
            className="t-btn-ghost mb-3 flex items-center gap-2"
            data-testid="admin-profile-btn"
          >
            <UserIcon size={14} strokeWidth={1.4} /> Settings
          </button>
          <button
            type="button"
            onClick={logout}
            className="t-btn-ghost flex items-center gap-2"
            data-testid="admin-logout-btn"
          >
            <LogOut size={14} strokeWidth={1.4} /> Sign out
          </button>
        </div>
      </aside>

      <main
        className={`neo-shell relative z-10 m-3 flex min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-x-clip lg:ml-2 ${
          fullHeightPage ? "pb-3 lg:pb-0" : "pb-3"
        }`}
      >
        <div
          ref={mainScrollRef}
          className={`relative flex min-h-0 w-full max-w-full flex-1 flex-col lg:overscroll-y-contain ${
            fullHeightPage ? "lg:overflow-hidden" : "lg:overflow-y-auto"
          }`}
        >
          <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col xl:mx-auto xl:max-w-[1200px]">
            <AnimatedOutlet fillHeight={fullHeightPage} scrollRootRef={mainScrollRef} />
          </div>
        </div>
      </main>

      <BLUFloatingChat />
    </div>
  );
};
